import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeDefault, selectionSummary, toggleSampleType } from '../../../utils/sampleTypeSelection';
import './SampleTypeMultiSelect.css';

/**
 * Pick one or more sample types for an investigation and mark one as the default.
 * `options` are the sample type master rows ({ sampleTypeId, sampleName }); `value` is { ids, defaultId }.
 */
const SampleTypeMultiSelect = ({ options, value, onChange, disabled = false, id = 'stm-select' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const nameOf = (sid) => options.find((o) => o.sampleTypeId === sid)?.sampleName || '';
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => (o.sampleName || '').toLowerCase().includes(q)) : options;
  }, [options, query]);
  const summary = selectionSummary(value.ids, value.defaultId, nameOf);

  return (
    <div className="stm" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`form-select stm-trigger ${open ? 'is-open' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={summary ? '' : 'stm-placeholder'}>{summary || '-- None / General --'}</span>
      </button>

      {value.ids.length > 0 && (
        <div className="stm-chips" aria-label="Selected sample types">
          {value.ids.map((sid) => (
            <span key={sid} className={`stm-chip ${sid === value.defaultId ? 'is-default' : ''}`}>
              {nameOf(sid)}{sid === value.defaultId ? ' · default' : ''}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="stm-panel" role="listbox" aria-multiselectable="true" aria-label="Sample matrix types">
          <input
            type="search"
            className="stm-search"
            placeholder="Search sample type…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search sample type"
            autoFocus
          />
          <ul className="stm-list">
            {shown.length === 0 && <li className="stm-empty">No sample type found.</li>}
            {shown.map((o) => {
              const checked = value.ids.includes(o.sampleTypeId);
              const isDefault = checked && value.defaultId === o.sampleTypeId;
              return (
                <li key={o.sampleTypeId} className={`stm-row ${checked ? 'is-checked' : ''}`}>
                  <label className="stm-check">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onChange(toggleSampleType(value.ids, value.defaultId, o.sampleTypeId))}
                    />
                    <span>{o.sampleName}</span>
                  </label>
                  {checked && (
                    <button
                      type="button"
                      className={`stm-default ${isDefault ? 'is-on' : ''}`}
                      onClick={() => onChange(makeDefault(value.ids, value.defaultId, o.sampleTypeId))}
                      aria-pressed={isDefault}
                      title={isDefault ? 'This is the default sample type' : 'Make this the default sample type'}
                    >
                      {isDefault ? '★ Default' : '☆ Make default'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="stm-foot">
            <span>{value.ids.length} selected</span>
            <button type="button" className="stm-done" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SampleTypeMultiSelect;
